import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WifiIcon from '@mui/icons-material/Wifi';
import BoltIcon from '@mui/icons-material/Bolt';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import HomeIcon from '@mui/icons-material/Home';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import { expensesApi } from '../../services/mybudgetApi';
import {
  Expense,
  ExpenseSuggestion,
  EXPENSE_TYPE_LABELS,
  ExpenseType,
} from '../../types';
import { useCategories } from '../../contexts/CategoriesContext';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { ActionButton } from '../../components/shared/ActionButton';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { CategoryBadge } from '../../components/shared/CategoryBadge';
import { useToaster } from '../../components/shared/Toaster';
import { formatCurrency, formatMonthYear, MONTHS } from '../../constants';

const SUGGESTION_ICONS: Record<string, React.ReactNode> = {
  wifi: <WifiIcon />,
  bolt: <BoltIcon />,
  water_drop: <WaterDropIcon />,
  home: <HomeIcon />,
  shopping_cart: <ShoppingCartIcon />,
  child_care: <ChildCareIcon />,
};

export function ExpensesPage() {
  const { categories, getDefaultCategoryId } = useCategories();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [suggestions, setSuggestions] = useState<ExpenseSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToaster();

  const [form, setForm] = useState({
    name: '',
    categoryId: 0,
    amount: '',
    expenseType: 'recurring' as ExpenseType,
    totalInstallments: '12',
    suggestionKey: '' as string,
    notes: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exp, sug] = await Promise.all([
        expensesApi.list(year, month) as Promise<Expense[]>,
        expensesApi.suggestions() as Promise<ExpenseSuggestion[]>,
      ]);
      setExpenses(exp);
      setSuggestions(sug);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const total = expenses.reduce((s, e) => s + Number(e.amount), 0);

  const openSuggestion = (s: ExpenseSuggestion) => {
    setForm({
      name: s.name,
      categoryId: s.categoryId ?? getDefaultCategoryId() ?? 0,
      amount: '',
      expenseType: 'recurring',
      totalInstallments: '12',
      suggestionKey: s.key,
      notes: '',
    });
    setModalOpen(true);
  };

  const openNew = () => {
    setForm({
      name: '',
      categoryId: getDefaultCategoryId() ?? categories[0]?.id ?? 0,
      amount: '',
      expenseType: 'one_time',
      totalInstallments: '12',
      suggestionKey: '',
      notes: '',
    });
    setModalOpen(true);
  };

  const handleCreate = async () => {
    if (!form.name || !form.amount) return;
    setSaving(true);
    try {
      await expensesApi.create({
        name: form.name,
        categoryId: form.categoryId,
        amount: Number(form.amount),
        year,
        month,
        expenseType: form.expenseType,
        totalInstallments:
          form.expenseType === 'installment'
            ? Number(form.totalInstallments)
            : undefined,
        suggestionKey: form.suggestionKey || undefined,
        notes: form.notes || undefined,
      });
      addToast({ type: 'success', message: 'Despesa adicionada!' });
      setModalOpen(false);
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remover esta despesa?')) return;
    try {
      await expensesApi.delete(id);
      addToast({ type: 'success', message: 'Despesa removida' });
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao remover',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Despesas</h2>
          <p className="text-sm text-gray-400">
            Categorize gastos, parcelas e contas fixas
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select
            options={Array.from({ length: 5 }, (_, i) => ({
              value: now.getFullYear() - i,
              label: String(now.getFullYear() - i),
            }))}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
          <Select
            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          />
          <ActionButton onClick={openNew}>
            <AddIcon fontSize="small" />
            Nova despesa
          </ActionButton>
        </div>
      </div>

      <Card title="Sugestões rápidas">
        <p className="mb-4 text-sm text-gray-400">
          Adicione rapidamente internet, energia, água e outras despesas comuns
        </p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {suggestions.map((s) => (
            <button
              key={s.key}
              onClick={() => openSuggestion(s)}
              className="flex items-center gap-3 rounded-xl border border-dark-border bg-dark-bg/50 p-4 text-left transition hover:border-accent/40 hover:bg-accent/5"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent/10 text-accent">
                {SUGGESTION_ICONS[s.icon] ?? <AddIcon />}
              </span>
              <div>
                <p className="font-medium text-gray-100">{s.name}</p>
                {s.category && <CategoryBadge category={s.category} />}
              </div>
            </button>
          ))}
        </div>
      </Card>

      <Card glow="expense">
        <p className="text-sm text-gray-400">Total em {formatMonthYear(year, month)}</p>
        <p className="mt-1 text-3xl font-bold text-expense">{formatCurrency(total)}</p>
      </Card>

      {loading ? (
        <Loading />
      ) : expenses.length === 0 ? (
        <Card>
          <p className="text-center text-gray-500">
            Nenhuma despesa neste mês. Use as sugestões ou crie uma nova.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {expenses.map((expense) => (
            <Card key={expense.id} className="!p-0">
              <div className="flex items-center justify-between px-5 py-4">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-gray-100">{expense.name}</p>
                    <CategoryBadge category={expense.category} />
                    <span className="rounded-full bg-dark-border px-2 py-0.5 text-xs text-gray-400">
                      {EXPENSE_TYPE_LABELS[expense.expenseType]}
                    </span>
                    {expense.installmentNumber && expense.totalInstallments && (
                      <span className="text-xs text-gray-500">
                        {expense.installmentNumber}/{expense.totalInstallments}
                      </span>
                    )}
                  </div>
                  {expense.notes && (
                    <p className="text-sm text-gray-500">{expense.notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-lg font-semibold text-expense">
                    {formatCurrency(Number(expense.amount))}
                  </span>
                  <button
                    onClick={() => handleDelete(expense.id)}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nova despesa"
        footer={
          <ModalActions
            onCancel={() => setModalOpen(false)}
            onConfirm={handleCreate}
            loading={saving}
          />
        }
      >
        <div className="space-y-4">
          <Input
            label="Nome"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <Select
            label="Categoria"
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            value={form.categoryId}
            onChange={(e) =>
              setForm({ ...form, categoryId: Number(e.target.value) })
            }
          />
          <Input
            label="Valor (R$)"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
          />
          <Select
            label="Tipo"
            options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            value={form.expenseType}
            onChange={(e) =>
              setForm({ ...form, expenseType: e.target.value as ExpenseType })
            }
          />
          {form.expenseType === 'installment' && (
            <Input
              label="Número de parcelas"
              type="number"
              min="2"
              max="60"
              value={form.totalInstallments}
              onChange={(e) =>
                setForm({ ...form, totalInstallments: e.target.value })
              }
            />
          )}
          <Input
            label="Observações (opcional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}
